// ============================================================================
// JARVISTRAVEL - SHARE API
// Share link creation and management
// JT-EXP-002
// ============================================================================

import { api, isDemoSession } from './client';
import { reportApiFailure } from './failure';

/**
 * A share link as core answers it.
 *
 * Every field here was read off a live response rather than from core's model:
 * `POST /v1/trips/{trip_id}/share` and `GET /v1/trips/{trip_id}/shares` both
 * carry exactly these seven keys (JAR-1527's live check, core `origin/main`).
 *
 * `scope` and `exclude_fields` used to sit here and are gone (JAR-2185). They
 * are columns on `share_links`, not fields in the answer, so `scope` was a
 * REQUIRED string that is `undefined` on every live object, typed as a
 * vocabulary (`'itinerary_only' | 'full'`) core does not use either — core
 * accepted `"journal"` into that column without complaint. Nothing read them,
 * which is the only reason a type this wrong never showed. If scoping is ever
 * served, it comes back carrying the values core actually sends.
 */
export interface ShareLink {
  id: string;
  trip_id: string;
  share_token: string;
  /** Core defaults this 30 days out, and the column is nullable, so optional. */
  expires_at?: string;
  revoked: boolean;
  created_at: string;
  created_by: string;
}

/**
 * Minting is `createJournalShareLink` in `./journal`, which is what both share
 * sheets call. A `createShareLink` here duplicated that endpoint with no callers
 * and its own wider type; deleted with JAR-2185 rather than kept as a second
 * contract for one route.
 */

// The demo's open links (JAR-1996). A demo link is the stand-in
// createJournalShareLink builds from the trip id, so it is recorded here when
// handed out, listed like a minted one, and stopped by forgetting it. Nothing
// leaves the browser.
const DEMO_SHARES_KEY = 'jt.share.links';

const readDemoShares = (): ShareLink[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(DEMO_SHARES_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const writeDemoShares = (links: ShareLink[]) => {
  try {
    localStorage.setItem(DEMO_SHARES_KEY, JSON.stringify(links));
  } catch {
    // storage full or unavailable: the link still copies, it just isn't listed
  }
};

/** Record a demo trip's stand-in link as open, once. */
export function openDemoShare(tripId: string, token: string): void {
  const links = readDemoShares();
  if (links.some((l) => l.share_token === token)) return;
  const now = new Date();
  const expires = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // core's default
  writeDemoShares([
    { id: `demo-share-${token}`, trip_id: tripId, share_token: token, expires_at: expires.toISOString(),
      revoked: false, created_at: now.toISOString(), created_by: 'demo' },
    ...links,
  ]);
}

/** Forget every demo link, when a demo starts or ends (demoDataPurge). */
export function clearDemoShares(): void {
  try {
    localStorage.removeItem(DEMO_SHARES_KEY);
  } catch {
    // nothing to clear
  }
}

/**
 * The trip's share links that still open, newest first.
 * GET /v1/trips/{trip_id}/shares (owner-only; core leaves out revoked and
 * expired links).
 *
 * Throws rather than answering an empty list (JAR-1568). Empty is what "nothing
 * is shared" looks like, so an unreadable list would tell the owner their
 * links are closed while they still open.
 */
export async function listShares(tripId: string): Promise<ShareLink[]> {
  if (isDemoSession()) return readDemoShares().filter((l) => l.trip_id === tripId);
  const result = await api.get<ShareLink[]>(`/trips/${encodeURIComponent(tripId)}/shares`);
  if (result.error) {
    reportApiFailure('[share] listShares failed:', result.error);
    throw result.error;
  }
  if (!Array.isArray(result.data)) throw new Error('Share links could not be read');
  return result.data;
}

/**
 * Stop a share link opening. POST /v1/shares/{share_id}/revoke (owner-only).
 *
 * Resolves only when core confirms the link is revoked, and throws otherwise
 * (JAR-1568), so a screen can never show a link as stopped while it still opens.
 */
export async function revokeShare(shareId: string): Promise<void> {
  if (isDemoSession()) {
    writeDemoShares(readDemoShares().filter((l) => l.id !== shareId));
    return;
  }
  const result = await api.post<{ revoked: boolean }>(`/shares/${encodeURIComponent(shareId)}/revoke`);
  if (result.error) {
    reportApiFailure('[share] revokeShare failed:', result.error);
    throw result.error;
  }
  // Strict, and coupled to core on purpose: HandleRevokeShare answers exactly
  // 200 {"revoked": true} on success and a JSON error otherwise, never a 204
  // and never another 2xx shape. So a 204, any non-200 status, or a demo
  // session's synthetic empty 200 throws here rather than reading as
  // "stopped". If core ever changes this contract, these two checks and the
  // pins below change with it (JAR-1576).
  if (result.status !== 200) throw new Error('The share link was not revoked');
  if (result.data?.revoked !== true) throw new Error('The share link was not revoked');
}
